/**
 * loginDesign — the single source of truth for how the login page looks.
 *
 * 2026-09-04 — owner: "אני רוצה שאוכל להחליף את התמונה דרך ההגדרות אבל
 *   גם שאוכל לשחק עם גודל התמונה … ותצוגה מקדימה … ואם אני רוצה לשים
 *   תמונה שתהיה על כל המסך ולהזיז את החלק של ההתחברות".
 *
 * Both the real login screen (components/LoginPage.js) and the admin
 * designer (components/setup/LoginPageDesigner.js) render from THIS
 * module. That is deliberate: a preview that builds its own CSS drifts
 * from the page it claims to preview, and the owner would only find out
 * after saving. One builder, two callers, no drift.
 *
 * The design lives in the typed feature flag `login.design`
 * (value_type=json), read publicly — the login screen is pre-auth, so it
 * cannot call an admin endpoint. See backend/routers/login_design.py.
 *
 * FAIL-OPEN CONTRACT: every consumer must render correctly when the flag
 * is missing, malformed, or the fetch fails. DEFAULT_DESIGN below IS the
 * screen as it looked before this feature shipped, so "no config" and
 * "config unreachable" both land on the known-good look.
 */

/** The pre-feature appearance, field by field. Changing a value here
 *  changes what every un-configured environment shows. */
export const DEFAULT_DESIGN = {
  // Layout ------------------------------------------------------------
  layout: 'split',            // 'split' | 'fullscreen'
  heroSide: 'right',          // which side the image sits on (split only)
  heroWidthPct: 55,           // % of viewport width taken by the image

  // Hero image --------------------------------------------------------
  heroImageUrl: '/assets/login_hero.png',
  heroFit: 'cover',           // 'cover' | 'contain' | 'fill'
  heroPositionX: 50,          // object-position X, 0=right … 100=left (RTL)
  heroPositionY: 50,          // object-position Y, 0=top … 100=bottom
  heroScale: 100,             // zoom %, 100 = no zoom

  // Video hero. A .mp4/.webm/.ogg URL in heroImageUrl renders a <video>
  // instead of an <img>; everything above (fit, position, zoom, overlay)
  // applies identically because both carry the same CSS class.
  heroPosterUrl: '',          // still shown while the video loads
  heroVideoLoop: true,

  // Overlay tint on top of the image ----------------------------------
  overlayColor: '#000000',
  overlayOpacity: 0,          // 0-100

  // Page --------------------------------------------------------------
  pageBg: '#f4f6f9',

  // The login card ----------------------------------------------------
  cardX: 50,                  // fullscreen only: % from the RIGHT edge
  cardY: 50,                  // fullscreen only: % from the top
  cardWidth: 420,             // px, the form column's max width
  cardBg: '#ffffff',
  cardOpacity: 100,           // 0-100
  cardRadius: 0,              // px — 0 keeps the flush split look
  cardShadow: true,
  cardPadding: 60,            // px
  cardFullHeight: false,      // fullscreen only: stretch the card top-to-bottom
  cardInsetPct: 2,            // gap from the top/bottom edge when stretched

  // Branding ----------------------------------------------------------
  logoUrl: '/assets/logo.png',
  logoWidth: 180,             // px
  title: 'התחברות למערכת',
  subtitle: 'הזן את פרטי ההתחברות שלך',
  titleColor: '#032d60',
  primaryColor: '#0176d3',

  // Responsive --------------------------------------------------------
  hideHeroOnMobile: true,
};

/** Numeric fields and their accepted range. Used by mergeDesign to clamp
 *  and by the backend validator to reject junk before it is stored. */
export const NUMERIC_BOUNDS = {
  heroWidthPct:   [10, 90],
  heroPositionX:  [0, 100],
  heroPositionY:  [0, 100],
  heroScale:      [100, 300],
  overlayOpacity: [0, 100],
  cardX:          [0, 100],
  cardY:          [0, 100],
  cardWidth:      [280, 900],
  cardOpacity:    [0, 100],
  cardRadius:     [0, 40],
  cardPadding:    [12, 120],
  cardInsetPct:   [0, 12],
  logoWidth:      [60, 400],
};

export const ENUM_VALUES = {
  layout:   ['split', 'fullscreen'],
  heroSide: ['right', 'left'],
  heroFit:  ['cover', 'contain', 'fill'],
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const COLOR_FIELDS = ['overlayColor', 'pageBg', 'cardBg', 'titleColor', 'primaryColor'];

/** Extensions we render through <video>. Everything else — including
 *  animated GIF and WebP, which <img> plays on its own — stays an image. */
const VIDEO_EXT_RE = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i;

/** True when this hero URL must be rendered as <video>. Exported so the
 *  login page and the admin preview branch on the same rule. */
export function isVideoUrl(url) {
  return typeof url === 'string' && VIDEO_EXT_RE.test(url.trim());
}

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/**
 * Merge a stored (possibly partial, possibly hostile) design onto the
 * defaults. Anything unrecognised, out of range, or of the wrong type
 * falls back to its default rather than reaching the stylesheet — a bad
 * value must not be able to render the login screen unusable.
 */
export function mergeDesign(stored) {
  const out = { ...DEFAULT_DESIGN };
  if (!stored || typeof stored !== 'object') return out;

  for (const [key, def] of Object.entries(DEFAULT_DESIGN)) {
    if (!(key in stored)) continue;
    const raw = stored[key];

    if (key in NUMERIC_BOUNDS) {
      const n = Number(raw);
      if (Number.isFinite(n)) out[key] = clamp(n, ...NUMERIC_BOUNDS[key]);
      continue;
    }
    if (key in ENUM_VALUES) {
      if (ENUM_VALUES[key].includes(raw)) out[key] = raw;
      continue;
    }
    if (COLOR_FIELDS.includes(key)) {
      if (typeof raw === 'string' && HEX_RE.test(raw.trim())) out[key] = raw.trim();
      continue;
    }
    if (typeof def === 'boolean') {
      out[key] = Boolean(raw);
      continue;
    }
    // Remaining fields are free text (urls, title, subtitle).
    if (typeof raw === 'string') out[key] = raw.slice(0, 500);
  }
  return out;
}

/** #rrggbb + 0-100 → rgba(). Kept local so callers never hand-roll it. */
function rgba(hex, opacityPct) {
  const h = HEX_RE.test(hex || '') ? hex : '#000000';
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${clamp(Number(opacityPct) || 0, 0, 100) / 100})`;
}

/**
 * Build the complete login stylesheet for one design.
 *
 * `scope` is prefixed onto every selector so the same CSS can be dropped
 * into the admin preview without leaking onto the Setup screen around
 * it. The real page passes the default; the designer passes its own
 * container class.
 */
export function buildLoginCss(designInput, scope = '.login-page') {
  const d = mergeDesign(designInput);
  const S = scope;
  const full = d.layout === 'fullscreen';

  // object-position: X is expressed right-to-left so the slider reads
  // naturally on an RTL screen (0 = ימין).
  const objPos = `${100 - d.heroPositionX}% ${d.heroPositionY}%`;
  const cardBg = rgba(d.cardBg, d.cardOpacity);
  const overlay = rgba(d.overlayColor, d.overlayOpacity);
  const shadow = d.cardShadow
    ? (full ? '0 12px 48px rgba(0,0,0,0.28)' : '-10px 0 40px rgba(0,0,0,0.1)')
    : 'none';

  // --- layout block: the only part that differs between the two modes.
  const layoutCss = full ? `
  ${S} {
    display: block;
    position: relative;
  }
  ${S} .login-hero {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    width: auto;
    z-index: 0;
  }
  ${S} .login-form-container {
    position: absolute;
    ${d.cardFullHeight
      ? `top: ${d.cardInsetPct}%; bottom: ${d.cardInsetPct}%; transform: translateX(50%);`
      : `top: ${d.cardY}%; transform: translate(50%, -50%);`}
    right: ${d.cardX}%;
    width: ${d.cardWidth + d.cardPadding * 2}px;
    max-width: calc(100vw - 32px);
    min-width: 0;
    z-index: 1;
    background: ${cardBg};
    border-radius: ${d.cardRadius}px;
    box-shadow: ${shadow};
    backdrop-filter: ${d.cardOpacity < 100 ? 'blur(10px)' : 'none'};
    overflow-y: auto;
  }
` : `
  ${S} {
    display: flex;
    flex-direction: ${d.heroSide === 'left' ? 'row-reverse' : 'row'};
  }
  ${S} .login-hero {
    flex: 0 0 ${d.heroWidthPct}%;
    width: ${d.heroWidthPct}%;
  }
  ${S} .login-form-container {
    flex: 1 1 auto;
    min-width: 0;
    background: ${cardBg};
    border-radius: ${d.cardRadius}px;
    box-shadow: ${shadow};
  }
`;

  return `
  ${S} {
    min-height: 100vh;
    direction: rtl;
    background: ${d.pageBg};
  }

${layoutCss}

  ${S} .login-hero {
    position: ${full ? 'absolute' : 'relative'};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  ${S} .hero-image {
    width: 100%;
    height: 100%;
    object-fit: ${d.heroFit};
    object-position: ${objPos};
    transform: scale(${d.heroScale / 100});
    display: block;
  }

  ${S} .hero-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: ${overlay};
    pointer-events: none;
  }

  ${S} .login-form-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${d.cardPadding}px;
    box-sizing: border-box;
  }

  ${S} .login-form-wrapper {
    width: 100%;
    max-width: ${d.cardWidth}px;
  }

  ${S} .login-logo {
    text-align: center;
    margin-bottom: 32px;
  }

  ${S} .company-logo {
    max-width: ${d.logoWidth}px;
    max-height: ${Math.round(d.logoWidth * 0.45)}px;
    object-fit: contain;
  }

  ${S} .company-logo:not(.loaded) { display: none; }

  ${S} .logo-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  ${S} .logo-fallback .logo-icon { font-size: 3rem; }
  ${S} .logo-fallback .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${d.titleColor};
  }

  ${S} .login-title { text-align: center; margin-bottom: 32px; }
  ${S} .login-title h2 {
    margin: 0 0 8px 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: ${d.titleColor};
  }
  ${S} .login-title p { margin: 0; color: #706e6b; font-size: 0.95rem; }

  ${S} .login-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #fef0f0;
    border: 1px solid #ea6e6c;
    border-radius: 8px;
    color: #c23934;
    margin-bottom: 20px;
    font-size: 0.9rem;
  }
  ${S} .error-icon { font-size: 1rem; }

  ${S} .login-form { display: flex; flex-direction: column; gap: 20px; }
  ${S} .form-group { display: flex; flex-direction: column; gap: 8px; }
  ${S} .form-group label { font-size: 0.9rem; font-weight: 600; color: #3e3e3c; }

  ${S} .input-wrapper { position: relative; display: flex; align-items: center; }
  ${S} .input-icon {
    position: absolute;
    right: 14px;
    font-size: 0.95rem;
    color: #706e6b;
    pointer-events: none;
    line-height: 1;
  }
  ${S} .input-wrapper input {
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #d8dde6;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.2s;
    background: #fafbfc;
    box-sizing: border-box;
  }
  ${S} .input-wrapper input:focus {
    outline: none;
    border-color: ${d.primaryColor};
    box-shadow: 0 0 0 3px ${rgba(d.primaryColor, 15)};
    background: white;
  }
  ${S} .input-wrapper input::placeholder { color: #b0adab; }

  ${S} .form-options {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
  }
  ${S} .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #3e3e3c;
  }
  ${S} .checkbox-label input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${d.primaryColor};
  }
  ${S} .forgot-password {
    color: ${d.primaryColor};
    text-decoration: none;
    font-weight: 500;
  }
  ${S} .forgot-password:hover { text-decoration: underline; }

  ${S} .login-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    background: ${d.primaryColor};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-top: 8px;
  }
  ${S} .login-button:hover:not(:disabled) {
    filter: brightness(0.9);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${rgba(d.primaryColor, 40)};
  }
  ${S} .login-button:active { transform: translateY(0); }
  ${S} .login-button:disabled { opacity: 0.7; cursor: not-allowed; }
  ${S} .login-button .arrow { font-size: 1.2rem; transition: transform 0.2s; }
  ${S} .login-button:hover .arrow { transform: translateX(-4px); }
  ${S} .login-button.loading .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: afk-login-spin 0.8s linear infinite;
  }

  @keyframes afk-login-spin { to { transform: rotate(360deg); } }

  ${S} .login-footer {
    text-align: center;
    margin-top: 32px;
    color: #706e6b;
    font-size: 0.9rem;
  }
  ${S} .login-footer a {
    color: ${d.primaryColor};
    text-decoration: none;
    font-weight: 500;
  }
  ${S} .login-footer a:hover { text-decoration: underline; }
  ${S} .footer-links {
    display: flex;
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
  }
  ${S} .footer-links span { color: #d8dde6; }

  @media (max-width: 1024px) {
    ${S} .login-hero { ${d.hideHeroOnMobile ? 'display: none;' : ''} }
    ${S} .login-form-container {
      ${full ? 'position: static; transform: none; width: 100%; max-width: 100%;' : 'width: 100%;'}
      min-width: auto;
      flex: 1 1 100%;
    }
    ${full ? `${S} { display: flex; align-items: center; justify-content: center; min-height: 100vh; }` : ''}
  }

  @media (max-width: 480px) {
    ${S} .login-form-container { padding: 24px; }
    ${S} .login-title h2 { font-size: 1.5rem; }
    ${S} .form-options { flex-direction: column; gap: 12px; align-items: flex-start; }
  }
`;
}

/** Field metadata driving the designer UI. Kept beside the schema so a
 *  new knob is one edit, not three. */
export const DESIGN_FIELDS = [
  { group: 'פריסה', key: 'layout', label: 'סוג פריסה', type: 'enum',
    options: [
      { value: 'split', label: 'מפוצל — תמונה בצד, טופס בצד' },
      { value: 'fullscreen', label: 'מסך מלא — תמונה ברקע, טופס מעליה' },
    ],
    hint: 'במסך מלא התמונה תופסת את כל החלון והטופס צף מעליה במיקום שתבחר.' },
  { group: 'פריסה', key: 'heroSide', label: 'צד התמונה', type: 'enum',
    options: [{ value: 'right', label: 'ימין' }, { value: 'left', label: 'שמאל' }],
    showIf: (d) => d.layout === 'split' },
  { group: 'פריסה', key: 'heroWidthPct', label: 'רוחב התמונה (% מהמסך)', type: 'range', unit: '%',
    showIf: (d) => d.layout === 'split' },

  { group: 'תמונה', key: 'heroImageUrl', label: 'מדיית רקע', type: 'image',
    hint: 'תמונה, GIF מונפש או וידאו קצר (mp4/webm). העלה קובץ או הדבק כתובת. ריק = ללא רקע.' },
  { group: 'תמונה', key: 'heroPosterUrl', label: 'תמונת פתיחה לוידאו', type: 'image',
    hint: 'מוצגת בזמן שהוידאו נטען, וגם למשתמשים שביקשו להפחית אנימציות.',
    showIf: (d) => isVideoUrl(d.heroImageUrl) },
  { group: 'תמונה', key: 'heroVideoLoop', label: 'הפעלה בלולאה', type: 'bool',
    hint: 'הוידאו תמיד מושתק — הדפדפנים מתירים ניגון אוטומטי רק ללא קול.',
    showIf: (d) => isVideoUrl(d.heroImageUrl) },
  { group: 'תמונה', key: 'heroFit', label: 'התאמת התמונה', type: 'enum',
    options: [
      { value: 'cover', label: 'מילוי — ממלא את השטח, חותך קצוות' },
      { value: 'contain', label: 'הכלה — התמונה כולה נראית' },
      { value: 'fill', label: 'מתיחה — נמתחת לכל השטח' },
    ] },
  { group: 'תמונה', key: 'heroScale', label: 'זום', type: 'range', unit: '%' },
  { group: 'תמונה', key: 'heroPositionX', label: 'מיקום אופקי (0=ימין)', type: 'range', unit: '%' },
  { group: 'תמונה', key: 'heroPositionY', label: 'מיקום אנכי (0=למעלה)', type: 'range', unit: '%' },
  { group: 'תמונה', key: 'overlayColor', label: 'צבע שכבת הכהיה', type: 'color' },
  { group: 'תמונה', key: 'overlayOpacity', label: 'עוצמת ההכהיה', type: 'range', unit: '%',
    hint: 'מכהה את התמונה כדי שטקסט מעליה ייקרא.' },

  { group: 'טופס ההתחברות', key: 'cardX', label: 'מיקום אופקי (0=ימין)', type: 'range', unit: '%',
    showIf: (d) => d.layout === 'fullscreen' },
  { group: 'טופס ההתחברות', key: 'cardFullHeight', label: 'מתיחת הטופס לגובה מלא', type: 'bool',
    hint: 'פאנל מקצה לקצה במקום כרטיס צף.',
    showIf: (d) => d.layout === 'fullscreen' },
  { group: 'טופס ההתחברות', key: 'cardInsetPct', label: 'מרווח מלמעלה ומלמטה', type: 'range', unit: '%',
    showIf: (d) => d.layout === 'fullscreen' && d.cardFullHeight },
  { group: 'טופס ההתחברות', key: 'cardY', label: 'מיקום אנכי (0=למעלה)', type: 'range', unit: '%',
    showIf: (d) => d.layout === 'fullscreen' && !d.cardFullHeight },
  { group: 'טופס ההתחברות', key: 'cardWidth', label: 'רוחב הטופס', type: 'range', unit: 'px' },
  { group: 'טופס ההתחברות', key: 'cardPadding', label: 'ריווח פנימי', type: 'range', unit: 'px' },
  { group: 'טופס ההתחברות', key: 'cardBg', label: 'צבע רקע', type: 'color' },
  { group: 'טופס ההתחברות', key: 'cardOpacity', label: 'אטימות הרקע', type: 'range', unit: '%',
    hint: 'פחות מ-100% נותן זכוכית מטושטשת מעל התמונה.' },
  { group: 'טופס ההתחברות', key: 'cardRadius', label: 'עיגול פינות', type: 'range', unit: 'px' },
  { group: 'טופס ההתחברות', key: 'cardShadow', label: 'צל', type: 'bool' },

  { group: 'מיתוג וטקסט', key: 'logoUrl', label: 'לוגו', type: 'image' },
  { group: 'מיתוג וטקסט', key: 'logoWidth', label: 'רוחב הלוגו', type: 'range', unit: 'px' },
  { group: 'מיתוג וטקסט', key: 'title', label: 'כותרת', type: 'text' },
  { group: 'מיתוג וטקסט', key: 'subtitle', label: 'כותרת משנה', type: 'text' },
  { group: 'מיתוג וטקסט', key: 'titleColor', label: 'צבע הכותרת', type: 'color' },
  { group: 'מיתוג וטקסט', key: 'primaryColor', label: 'צבע ראשי (כפתור וקישורים)', type: 'color' },
  { group: 'מיתוג וטקסט', key: 'pageBg', label: 'צבע רקע הדף', type: 'color' },
  { group: 'מיתוג וטקסט', key: 'hideHeroOnMobile', label: 'הסתר תמונה במובייל', type: 'bool',
    hint: 'מתחת ל-1024 פיקסלים רוחב.' },
];

/**
 * Ready-made starting points. A preset is a normal partial design —
 * applying one just merges it over the current values, so the owner can
 * pick a look and then keep adjusting. None of them touch the form
 * markup; they only restyle the frame around it.
 */
export const PRESETS = [
  {
    id: 'classic-split',
    label: 'מפוצל קלאסי',
    description: 'התמונה בצד ימין, הטופס על רקע לבן בצד שמאל. זו התצוגה המקורית.',
    design: {
      layout: 'split', heroSide: 'right', heroWidthPct: 55, heroFit: 'cover',
      cardBg: '#ffffff', cardOpacity: 100, cardRadius: 0, cardPadding: 60,
      cardWidth: 420, cardShadow: true, overlayOpacity: 0,
    },
  },
  {
    id: 'glass-panel',
    label: 'זכוכית על מסך מלא',
    description: 'התמונה על כל המסך, פאנל שקוף-למחצה בגובה מלא בצד שמאל.',
    design: {
      layout: 'fullscreen', heroFit: 'cover', heroScale: 100,
      heroPositionX: 50, heroPositionY: 50,
      overlayColor: '#ffffff', overlayOpacity: 0,
      cardX: 76, cardFullHeight: true, cardInsetPct: 2,
      cardWidth: 370, cardPadding: 48, cardRadius: 18,
      cardBg: '#ffffff', cardOpacity: 92, cardShadow: true,
      logoUrl: '/assets/afikim_connect_logo.png', logoWidth: 190,
    },
  },
  {
    id: 'floating-center',
    label: 'כרטיס צף במרכז',
    description: 'התמונה על כל המסך והטופס כרטיס מרחף במרכז.',
    design: {
      layout: 'fullscreen', heroFit: 'cover',
      overlayColor: '#000000', overlayOpacity: 25,
      cardX: 50, cardY: 50, cardFullHeight: false,
      cardWidth: 400, cardPadding: 44, cardRadius: 20,
      cardBg: '#ffffff', cardOpacity: 96, cardShadow: true,
    },
  },
];
